import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface GpSummaryData {
  dependent: { id: string; name: string; dateOfBirth?: string };
  emergencyProfile?: {
    conditions?: string; allergies?: string; bloodType?: string;
    gpName?: string; gpPhone?: string; nhsNumber?: string;
    nextOfKinName?: string; nextOfKinPhone?: string; hospitalPreference?: string;
  };
  medications: Array<{
    id: string; name: string; dose?: string; timesPerDay: number; daysOfWeek: string[];
    adherence: { taken: number; missed: number; percentage: number | null };
  }>;
  appointments: Array<{ id: string; title: string; dateTime: string; location?: string }>;
  journalEntries?: Array<{ id: string; content: string; author: { name: string }; createdAt: string }>;
  generatedAt: string;
  periodWeeks: number;
}

const PERIOD_OPTIONS: Array<{ label: string; weeks: number }> = [
  { label: '2w', weeks: 2 },
  { label: '4w', weeks: 4 },
  { label: '8w', weeks: 8 },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDaysOfWeek(days: string[]): string {
  if (days.length === 7) return 'every day';
  const abbr: Record<string, string> = {
    MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
    THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun',
  };
  return days.map((d) => abbr[d] ?? d).join(', ');
}

export function GpSummaryPage() {
  const { dependentId } = useParams<{ dependentId: string }>();
  const navigate = useNavigate();

  const [familyId, setFamilyId] = useState('');
  const [data, setData] = useState<GpSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(4);
  const [includeJournal, setIncludeJournal] = useState(false);
  const [error, setError] = useState('');

  // Resolve familyId on mount
  useEffect(() => {
    api.getFamilies().then((families: any[]) => {
      if (families?.length) setFamilyId(families[0].id);
    }).catch(() => setError('Unable to load. Please refresh the page.'));
  }, []);

  // Fetch summary whenever familyId / weeks / includeJournal changes
  useEffect(() => {
    if (!familyId || !dependentId) return;
    setLoading(true);
    setError('');
    api.getGpSummary(familyId, dependentId, { weeks, includeJournal })
      .then((d: any) => setData(d))
      .catch(() => setError('Unable to load summary.'))
      .finally(() => setLoading(false));
  }, [familyId, dependentId, weeks, includeJournal]);

  const ep = data?.emergencyProfile;
  const hasProfile = ep && (ep.conditions || ep.allergies || ep.bloodType || ep.nhsNumber || ep.gpName || ep.nextOfKinName || ep.hospitalPreference);

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Toolbar — hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-gray-500 hover:text-gray-900 shrink-0"
        >
          ← Back
        </button>

        <span className="text-sm font-medium text-gray-900 shrink-0">GP Summary</span>
        {data && <span className="text-sm text-gray-400">— {data.dependent.name}</span>}

        <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5 ml-auto">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.weeks}
              onClick={() => setWeeks(opt.weeks)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${weeks === opt.weeks ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={includeJournal}
            onChange={(e) => setIncludeJournal(e.target.checked)}
            className="rounded"
          />
          Include journal
        </label>

        <button
          onClick={() => window.print()}
          className="shrink-0 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
        >
          Print
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
          Loading…
        </div>
      )}
      {error && !loading && (
        <div className="flex items-center justify-center py-24 text-red-500 text-sm">{error}</div>
      )}

      {data && !loading && (
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-6 print:px-0 print:py-4 print:max-w-none">
          {/* Print header */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary-600 mb-1 print:text-gray-500">Kin · GP Summary</p>
                <h1 className="text-2xl font-bold text-gray-900">{data.dependent.name}</h1>
                {data.dependent.dateOfBirth && (
                  <p className="text-sm text-gray-500 mt-0.5">DOB: {formatDate(data.dependent.dateOfBirth)}</p>
                )}
              </div>
              <div className="text-right text-xs text-gray-400">
                <p>Period: last {data.periodWeeks} week{data.periodWeeks !== 1 ? 's' : ''}</p>
                <p>Generated: {formatDate(data.generatedAt)}</p>
                <p className="mt-1 text-gray-300">CONFIDENTIAL CARE RECORD</p>
              </div>
            </div>
          </div>

          {/* Emergency profile */}
          {hasProfile && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Emergency Profile</h2>
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2 print:rounded-none print:border-gray-300">
                {(ep?.bloodType || ep?.nhsNumber) && (
                  <div className="flex gap-3 flex-wrap">
                    {ep?.bloodType && (
                      <span className="text-sm"><span className="font-medium text-gray-700">Blood type:</span> {ep.bloodType}</span>
                    )}
                    {ep?.nhsNumber && (
                      <span className="text-sm"><span className="font-medium text-gray-700">NHS:</span> {ep.nhsNumber}</span>
                    )}
                  </div>
                )}
                {ep?.conditions && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Conditions</span>
                    <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{ep.conditions}</p>
                  </div>
                )}
                {ep?.allergies && (
                  <div>
                    <span className="text-xs font-medium text-red-500 uppercase tracking-wide">⚠ Allergies</span>
                    <p className="text-sm text-red-800 mt-0.5 font-medium whitespace-pre-wrap">{ep.allergies}</p>
                  </div>
                )}
                {(ep?.gpName || ep?.gpPhone) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">GP</span>
                    <p className="text-sm text-gray-800 mt-0.5">{[ep.gpName, ep.gpPhone].filter(Boolean).join(' · ')}</p>
                  </div>
                )}
                {(ep?.nextOfKinName || ep?.nextOfKinPhone) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Next of kin</span>
                    <p className="text-sm text-gray-800 mt-0.5">{[ep.nextOfKinName, ep.nextOfKinPhone].filter(Boolean).join(' · ')}</p>
                  </div>
                )}
                {ep?.hospitalPreference && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hospital preference</span>
                    <p className="text-sm text-gray-800 mt-0.5">{ep.hospitalPreference}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Medications */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Medications · Last {data.periodWeeks} week{data.periodWeeks !== 1 ? 's' : ''} adherence
            </h2>
            {data.medications.length === 0 ? (
              <p className="text-sm text-gray-400">No medications recorded.</p>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 print:rounded-none print:border-gray-300">
                {data.medications.map((med) => (
                  <div key={med.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {med.name}{med.dose ? ` ${med.dose}` : ''}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {med.timesPerDay === 1 ? 'Once daily' : med.timesPerDay === 2 ? 'Twice daily' : `${med.timesPerDay}× daily`}
                          {' · '}{formatDaysOfWeek(med.daysOfWeek)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        {med.adherence.percentage !== null ? (
                          <>
                            <p className={`text-sm font-semibold ${med.adherence.percentage >= 80 ? 'text-green-600' : med.adherence.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                              {med.adherence.percentage}%
                            </p>
                            <p className="text-xs text-gray-400">{med.adherence.taken} taken · {med.adherence.missed} missed</p>
                          </>
                        ) : (
                          <p className="text-xs text-gray-400">No logs yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Appointments */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Appointments</h2>
            {data.appointments.length === 0 ? (
              <p className="text-sm text-gray-400">No appointments in this period.</p>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 print:rounded-none print:border-gray-300">
                {data.appointments.map((appt) => (
                  <div key={appt.id} className="px-4 py-3 flex gap-4">
                    <div className="text-xs text-gray-400 shrink-0 w-20 pt-0.5">{formatDate(appt.dateTime)}</div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{appt.title}</p>
                      {appt.location && <p className="text-xs text-gray-400 mt-0.5">{appt.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Journal entries (optional) */}
          {data.journalEntries && data.journalEntries.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                Observations ({data.journalEntries.length} care journal {data.journalEntries.length === 1 ? 'entry' : 'entries'})
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 print:rounded-none print:border-gray-300">
                {data.journalEntries.map((entry) => (
                  <div key={entry.id} className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-600">{entry.author.name}</span>
                      <span className="text-xs text-gray-400">{formatDate(entry.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{entry.content}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Print footer */}
          <div className="border-t border-gray-100 pt-4 text-center">
            <p className="text-xs text-gray-300">
              Kin · kinapp.co · Confidential care record · {formatDate(data.generatedAt)}
            </p>
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 1.5cm; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
